import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { API_BASE, apiFetch } from "../lib/api";

interface ConversationState {
  id: string;
  familyId: string;
  childId?: string;
  beat: number;
  recommendedTier: "basic" | "advanced" | "premium" | null;
  recommendationReasons: string[];
  objections: Array<{ id: string; label: string; resolved: boolean }>;
  outcome: string | null;
  managerNotes: string | null;
  parentResponses: Record<string, string>;
  committed: boolean;
}

const BEATS = [
  { n: 1, name: "Greet", script: "Welcome. Let's spend 90 seconds on what we found." },
  { n: 2, name: "Reflect findings", script: "Walk through each finding plainly." },
  { n: 3, name: "Tier explanation", script: "Why this tier — what's included." },
  { n: 4, name: "Objection probe", script: "Anything in the way? Spouse, budget, timing." },
  { n: 5, name: "Recommendation", script: "Confirm tier; if waiting, schedule spousal call." },
  { n: 6, name: "Close", script: "Subscribe today, or set the next step." },
];

function Activation() {
  const { conversationId } = Route.useParams();
  const [state, setState] = useState<ConversationState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const wsUrl = `${API_BASE.replace("https://", "wss://")}/api/conversations/${conversationId}/ws`;
    // Browsers can't set custom WebSocket headers — the api-worker accepts the
    // dev-token as a query param fallback OR (preferred) Cloudflare Access JWT
    // is auto-attached as a cookie when Access is on. For dev now, we pass via
    // the URL token query.
    const token = localStorage.getItem("skids.devToken") ?? "";
    const role = localStorage.getItem("skids.devRole") ?? "manager";
    const ws = new WebSocket(`${wsUrl}?dev_token=${encodeURIComponent(token)}&dev_role=${role}`);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      ws.send(JSON.stringify({ type: "hello" }));
    };
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setError("WebSocket error — see console");
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(e.data);
        if (m.type === "state") setState(m.state);
        if (m.type === "error") setError(m.message);
      } catch {}
    };
    return () => ws.close();
  }, [conversationId]);

  // Pull initial state once via REST snapshot in case WS races
  useEffect(() => {
    apiFetch<ConversationState | null>(`/api/conversations/${conversationId}/state`)
      .then((s) => {
        if (s && !state) setState(s);
      })
      .catch(() => {});
  }, [conversationId]);

  const send = (msg: object) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(msg));
  };

  const advance = (beat: number, parentResponse?: string) =>
    send({ type: "advanceBeat", beat, parentResponse });

  const addObjection = (label: string) =>
    send({ type: "addObjection", objectionId: `ob_${Date.now()}`, label });

  const resolveObjection = (id: string) =>
    send({ type: "resolveObjection", objectionId: id });

  const setTier = async (tier: "basic" | "advanced" | "premium") => {
    // Compute reasons via the backend tier engine for consistency
    try {
      const r = await apiFetch<{ tier: string; reasons: string[] }>(
        "/api/recommendations/tier",
        {
          method: "POST",
          body: JSON.stringify({
            childAge: 7,
            findings: [{ category: "vision", severity: "mild" }],
          }),
        },
      );
      send({ type: "setRecommendedTier", tier, reasons: r.reasons });
    } catch {
      send({ type: "setRecommendedTier", tier, reasons: ["manual override"] });
    }
  };

  const commit = (outcome: string) => {
    send({ type: "setOutcome", outcome, notes: state?.managerNotes ?? "" });
    setTimeout(() => send({ type: "commit" }), 200);
  };

  if (!state) {
    return (
      <div className="panel">
        <h1 className="text-2xl font-semibold mb-3">Activation Conversation</h1>
        <div className="text-sm text-slate-500">
          {error ? <span className="text-red-600">{error}</span> : "connecting…"}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">
              Screen 3
            </div>
            <h1 className="text-2xl font-semibold">Activation</h1>
          </div>
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              connected ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"
            }`}
          >
            {connected ? "live" : "offline"}
          </span>
        </div>

        <div className="flex gap-2 mb-4">
          {BEATS.map((b) => (
            <div
              key={b.n}
              className={`flex-1 text-center py-2 rounded text-xs ${
                state.beat >= b.n
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {b.n}. {b.name}
            </div>
          ))}
        </div>

        <CurrentBeat
          state={state}
          onAdvance={advance}
          disabled={state.committed}
        />

        <div className="mt-6 border-t pt-4">
          <h3 className="font-medium mb-2 text-sm">Objections</h3>
          <div className="flex flex-wrap gap-2 mb-2">
            {state.objections.map((o) => (
              <button
                key={o.id}
                onClick={() => resolveObjection(o.id)}
                disabled={o.resolved || state.committed}
                className={`text-xs px-2 py-1 rounded ${
                  o.resolved
                    ? "bg-green-100 text-green-700 line-through"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {o.label}
              </button>
            ))}
            <ObjectionAdd onAdd={addObjection} disabled={state.committed} />
          </div>
        </div>

        <div className="mt-6 border-t pt-4 flex gap-2 flex-wrap">
          <button
            disabled={state.committed}
            onClick={() => commit("subscribed")}
            className="rounded bg-slate-900 text-white px-3 py-2 text-sm disabled:opacity-50"
          >
            Close → Subscribed
          </button>
          <button
            disabled={state.committed}
            onClick={() => commit("spousal_call_scheduled")}
            className="rounded bg-slate-100 text-slate-800 px-3 py-2 text-sm disabled:opacity-50"
          >
            Spousal call
          </button>
          <button
            disabled={state.committed}
            onClick={() => commit("undecided")}
            className="rounded bg-slate-100 text-slate-800 px-3 py-2 text-sm disabled:opacity-50"
          >
            Undecided
          </button>
          <button
            disabled={state.committed}
            onClick={() => commit("not_interested")}
            className="rounded bg-slate-100 text-slate-800 px-3 py-2 text-sm disabled:opacity-50"
          >
            Not interested
          </button>
          {state.committed && (
            <Link
              to="/family/$familyId"
              params={{ familyId: state.familyId }}
              className="ml-auto text-sm text-slate-600 underline"
            >
              View family record →
            </Link>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="panel">
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">
            Tier recommendation
          </div>
          <div className="text-3xl font-semibold mb-2">
            {state.recommendedTier ?? "—"}
          </div>
          <div className="flex gap-1 mb-3">
            {(["basic", "advanced", "premium"] as const).map((t) => (
              <button
                key={t}
                disabled={state.committed}
                onClick={() => setTier(t)}
                className={`text-xs px-2 py-1 rounded ${
                  state.recommendedTier === t
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <ul className="text-xs text-slate-600 space-y-1">
            {state.recommendationReasons.map((r, i) => (
              <li key={i}>· {r}</li>
            ))}
          </ul>
          <div className="border-t mt-3 pt-3 text-xs text-slate-400">
            Conversation <code>{state.id}</code>
            <br />
            Family <code>{state.familyId}</code>
            <br />
            {state.committed && (
              <span className="text-green-700 font-medium">committed ✓</span>
            )}
          </div>
        </div>

        <ProtocolPanel />
      </div>
    </div>
  );
}

function ProtocolPanel() {
  const [protocolId, setProtocolId] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [ids, setIds] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ ids: string[] }>("/api/manager-cards")
      .then((r) => setIds(r.ids))
      .catch((e: Error) => setErr(e.message));
  }, []);

  useEffect(() => {
    if (!protocolId) { setBody(""); return; }
    apiFetch<{ protocolId: string; body: string }>(`/api/manager-cards/${protocolId}`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => setBody(r.body))
      .catch((e: Error) => setErr(e.message));
  }, [protocolId]);

  return (
    <div className="panel">
      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">
        Protocol manager-card
      </div>
      <div className="text-xs text-slate-500 mb-2">
        Pull up the SKIDS clinical guidance for any flagged finding while you're
        speaking with the parent.
      </div>
      {err && <div className="text-xs text-red-600 mb-1">{err}</div>}
      <select
        value={protocolId}
        onChange={(e) => setProtocolId(e.target.value)}
        className="w-full text-sm border border-slate-300 rounded px-2 py-1 mb-2"
      >
        <option value="">— pick a protocol —</option>
        {ids.map((id) => (
          <option key={id} value={id}>{id}</option>
        ))}
      </select>
      {body && (
        <div className="prose-sm border-t pt-2 max-h-96 overflow-y-auto text-xs whitespace-pre-wrap font-mono leading-relaxed">
          {body}
        </div>
      )}
    </div>
  );
}

function CurrentBeat({
  state,
  onAdvance,
  disabled,
}: {
  state: ConversationState;
  onAdvance: (n: number, response?: string) => void;
  disabled: boolean;
}) {
  const next = Math.min(6, state.beat + 1);
  const beatDef = BEATS[next - 1]!;
  const [response, setResponse] = useState("");
  return (
    <div>
      <div className="text-xs text-slate-400">Next beat</div>
      <h2 className="text-lg font-medium mb-1">
        {beatDef.n}. {beatDef.name}
      </h2>
      <div className="text-sm text-slate-600 italic mb-3">{beatDef.script}</div>
      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        rows={2}
        disabled={disabled}
        className="w-full border border-slate-300 rounded px-2 py-1 mb-2"
        placeholder="Parent response (optional)…"
      />
      <button
        disabled={disabled}
        onClick={() => {
          onAdvance(next, response || undefined);
          setResponse("");
        }}
        className="rounded bg-slate-900 text-white px-3 py-1 text-sm disabled:opacity-50"
      >
        Advance to beat {next}
      </button>
    </div>
  );
}

function ObjectionAdd({
  onAdd,
  disabled,
}: {
  onAdd: (label: string) => void;
  disabled: boolean;
}) {
  const [v, setV] = useState("");
  return (
    <form
      className="flex gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) onAdd(v.trim());
        setV("");
      }}
    >
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        disabled={disabled}
        placeholder="+ objection"
        className="text-xs border border-slate-300 rounded px-2 py-1"
      />
    </form>
  );
}

export const Route = createFileRoute("/activation/$conversationId")({
  component: Activation,
  validateSearch: (s: Record<string, unknown>) => ({
    familyId: typeof s.familyId === "string" ? s.familyId : undefined,
    childId: typeof s.childId === "string" ? s.childId : undefined,
  }),
});
